import { useState } from 'react';
import { Checkbox, Dropdown, Modal, Tooltip, Typography, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import type { OrderItem, FulfillmentMode } from '../types';
import { copyLineValues, type CopyLineColumns, type CopyLineSkipped } from '../utils/copyLineValues';

/**
 * TCR-2425 — chuột phải vào MỘT dòng hàng (đơn B2B) → menu "Sao chép dữ liệu
 * cho các dòng bên dưới" → hộp tick chọn cột (Số lượng / Đơn giá / Chiết khấu)
 * → ghi đè mọi dòng NẰM SAU dòng đó, đúng như AMIS.
 *
 * Vì sao chỉ bật ở đơn B2B: đơn tổ chức (MT/GT) có hàng chục dòng cùng một cấp
 * giá/chiết khấu — nhập tay từng dòng là nguồn sai số lớn nhất của bảng giá.
 * Đơn bán lẻ mỗi dòng một giá nên sao chép hàng loạt chỉ tạo tai nạn.
 *
 * Toàn bộ phép tính nằm ở `copyLineValues` (hàm thuần, dùng CHUNG công thức với
 * handler sửa tay trong `OrderItemRow`); component này chỉ lo menu + hộp thoại +
 * thông báo. Không truyền `items` xuống đây thì không biết dòng nào "bên dưới".
 */
export interface CopyLineValuesMenuProps {
  /** Dòng được chuột phải. */
  item: OrderItem;
  /** Toàn bộ dòng của giỏ theo thứ tự hiển thị. */
  items: OrderItem[];
  fulfillmentMode: FulfillmentMode;
  isB2B: boolean;
  hasDiscountPermission: boolean;
  lockFlashSaleLines?: boolean;
  maxQuantityFor?: (item: OrderItem) => number | undefined;
  onUpdateItem: (id: string, patch: Partial<OrderItem>) => void;
  /** Tiền tố data-testid — bảng desktop và danh sách mobile cùng render trong DOM. */
  idPrefix?: string;
  children: React.ReactNode;
}

const MENU_LABEL = 'Sao chép dữ liệu cho các dòng bên dưới';
const DIALOG_TITLE = 'Chọn cột sao chép dữ liệu';
const NO_TARGET_MESSAGE = 'Không có dòng nào bên dưới để sao chép';
const NO_PERMISSION_HINT = 'Bạn không có quyền sửa giá/chiết khấu';

/**
 * Gộp lý do bỏ qua cho message cảnh báo: cùng lý do thì gộp số lượng
 * ("Dòng quà tặng (2)"), khác lý do thì liệt kê cạnh nhau.
 */
export function summarizeSkipReasons(skipped: CopyLineSkipped[]): string {
  const countByReason = new Map<string, number>();
  for (const entry of skipped) {
    countByReason.set(entry.reason, (countByReason.get(entry.reason) ?? 0) + 1);
  }
  return Array.from(countByReason.entries())
    .map(([reason, count]) => (count > 1 ? `${reason} (${count})` : reason))
    .join(', ');
}

export function CopyLineValuesMenu({
  item,
  items,
  fulfillmentMode,
  isB2B,
  hasDiscountPermission,
  lockFlashSaleLines,
  maxQuantityFor,
  onUpdateItem,
  idPrefix = '',
  children,
}: CopyLineValuesMenuProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  // Mặc định tick Số lượng (thao tác phổ biến nhất); Đơn giá/Chiết khấu để
  // người dùng chủ động tick vì chúng đụng tiền.
  const [columns, setColumns] = useState<CopyLineColumns>({
    quantity: true,
    price: false,
    discount: false,
  });

  // Không phải B2B ⇒ KHÔNG bọc, KHÔNG gắn onContextMenu: menu chuột phải mặc
  // định của trình duyệt (copy/paste trong ô nhập) phải còn nguyên.
  if (!isB2B) {
    return <>{children}</>;
  }

  /** Các dòng đích: nằm SAU dòng này, theo thứ tự hiển thị, bỏ qua dòng combo. */
  const sourceIndex = items.findIndex((candidate) => candidate.id === item.id);
  const targets = items
    .slice(sourceIndex === -1 ? items.length : sourceIndex + 1)
    .filter((candidate) => !candidate.comboGroupId);

  const openDialog = () => {
    if (targets.length === 0) {
      void message.info(NO_TARGET_MESSAGE);
      return;
    }
    // Mỗi lần mở lại trả về mặc định (chỉ tick Số lượng): hộp này đụng tiền,
    // giữ nguyên lựa chọn của lần trước dễ khiến thu ngân bấm "Sao chép" mà
    // không nhìn lại các ô đã tick.
    setColumns({ quantity: true, price: false, discount: false });
    setDialogOpen(true);
  };

  const handleCopy = () => {
    const result = copyLineValues({
      source: item,
      targets,
      columns,
      fulfillmentMode,
      isB2B,
      hasDiscountPermission,
      maxQuantityFor,
      lockFlashSaleLines,
    });
    // Gọi `onUpdateItem` cho từng dòng CÓ patch — không gọi cho dòng không đổi.
    for (const [id, patch] of Object.entries(result.patches)) {
      onUpdateItem(id, patch);
    }
    if (result.applied.length > 0) {
      void message.success(`Đã sao chép cho ${result.applied.length} dòng`);
    }
    if (result.skipped.length > 0) {
      void message.warning(
        `Bỏ qua ${result.skipped.length} dòng: ${summarizeSkipReasons(result.skipped)}`,
      );
    }
    if (result.applied.length === 0 && result.skipped.length === 0) {
      // Mọi dòng đích đã đúng giá trị của dòng nguồn — không có gì để ghi.
      void message.info('Các dòng bên dưới đã có cùng giá trị, không cần sao chép');
    }
    setDialogOpen(false);
  };

  const setColumn = (key: keyof CopyLineColumns, value: boolean) => {
    setColumns((prev) => ({ ...prev, [key]: value }));
  };

  const noColumnSelected = !columns.quantity && !columns.price && !columns.discount;

  /**
   * Ô Đơn giá/Chiết khấu khoá khi thiếu quyền `pos.discount` — cùng chính sách
   * với input của dòng hàng, để không có đường vòng ghi đè giá/CK.
   */
  const gatedCheckbox = (key: 'price' | 'discount', label: string) => {
    const checkbox = (
      <Checkbox
        checked={columns[key]}
        disabled={!hasDiscountPermission}
        onChange={(event) => setColumn(key, event.target.checked)}
      >
        {label}
      </Checkbox>
    );
    // Tooltip trên phần tử disabled không nhận sự kiện chuột ⇒ bọc span.
    return hasDiscountPermission ? (
      checkbox
    ) : (
      <Tooltip title={NO_PERMISSION_HINT}>
        <span>{checkbox}</span>
      </Tooltip>
    );
  };

  return (
    <>
      <Dropdown
        trigger={['contextMenu']}
        menu={{
          items: [{ key: 'copy-line-values', icon: <CopyOutlined />, label: MENU_LABEL }],
          onClick: ({ key }) => {
            if (key === 'copy-line-values') openDialog();
          },
        }}
      >
        <div data-testid={`${idPrefix}pos-item-context-${item.id}`}>{children}</div>
      </Dropdown>
      <Modal
        open={dialogOpen}
        title={DIALOG_TITLE}
        okText="Sao chép"
        cancelText="Hủy"
        okButtonProps={{ disabled: noColumnSelected }}
        onOk={handleCopy}
        onCancel={() => setDialogOpen(false)}
        destroyOnHidden
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 4 }}>
          {/* Nhắc lại dòng nguồn để thu ngân không nhầm khi bảng đã cuộn. */}
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Lấy dữ liệu từ dòng #{sourceIndex + 1} — {item.variantName}
          </Typography.Text>
          <Checkbox
            checked={columns.quantity}
            onChange={(event) => setColumn('quantity', event.target.checked)}
          >
            Số lượng
          </Checkbox>
          {gatedCheckbox('price', 'Đơn giá')}
          {gatedCheckbox('discount', 'Chiết khấu')}
        </div>
      </Modal>
    </>
  );
}
